import React, { useState, useEffect, useCallback } from 'react';
import { getRoles, getRoleModules, addRole, updateRole, deleteRole } from '../api';
import { FiShield, FiX } from 'react-icons/fi';
import { usePermissions } from '../utils/permissions';

const ACTIONS = [
  { key: 'can_view', label: 'View' },
  { key: 'can_create', label: 'Create' },
  { key: 'can_edit', label: 'Edit' },
  { key: 'can_delete', label: 'Delete' },
];

function emptyPermissions(modules) {
  const p = {};
  modules.forEach(m => { p[m.key] = { can_view: false, can_create: false, can_edit: false, can_delete: false }; });
  return p;
}

export default function Roles() {
  const { can } = usePermissions();
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, mRes] = await Promise.all([getRoles(), getRoleModules()]);
      setRoles(rRes.data);
      setModules(mRes.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load roles');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null); setName(''); setDescription('');
    setPermissions(emptyPermissions(modules));
    setError(''); setShowModal(true);
  };

  const openEdit = (role) => {
    setEditing(role); setName(role.name); setDescription(role.description || '');
    const p = emptyPermissions(modules);
    (role.permissions || []).forEach(perm => {
      p[perm.module] = { can_view: !!perm.can_view, can_create: !!perm.can_create, can_edit: !!perm.can_edit, can_delete: !!perm.can_delete };
    });
    setPermissions(p);
    setError(''); setShowModal(true);
  };

  const togglePerm = (moduleKey, action) => {
    setPermissions(p => ({ ...p, [moduleKey]: { ...p[moduleKey], [action]: !p[moduleKey]?.[action] } }));
  };

  const toggleAllForModule = (moduleKey, value) => {
    setPermissions(p => ({ ...p, [moduleKey]: { can_view: value, can_create: value, can_edit: value, can_delete: value } }));
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Role name is required'); return; }
    setSaving(true); setError('');
    try {
      if (editing) await updateRole(editing.id, { name, description, permissions });
      else await addRole({ name, description, permissions });
      setShowModal(false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save role');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await deleteRole(id);
      setDeleteConfirm(null);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to delete role');
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title"><FiShield /> Roles &amp; Permissions</h2>
          <p style={{ color: 'var(--text-muted)', margin: '4px 0 0' }}>Create roles with granular per-module access, then assign them to staff under Admin Users</p>
        </div>
        {can('roles_users', 'create') && <button className="btn btn-primary" onClick={openAdd}>+ Add Role</button>}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>Role</th><th>Description</th><th>Modules Granted</th><th>Actions</th></tr></thead>
            <tbody>
              {roles.map(r => {
                const grantedCount = (r.permissions || []).filter(p => p.can_view || p.can_create || p.can_edit || p.can_delete).length;
                return (
                  <tr key={r.id}>
                    <td><strong>{r.name}</strong> {!!r.is_system && <span className="badge badge-gold" style={{ marginLeft: '6px' }}>System</span>}</td>
                    <td>{r.description || '—'}</td>
                    <td>{grantedCount} / {modules.length} modules</td>
                    <td>
                      <div className="action-buttons">
                        {can('roles_users', 'edit') && <button className="btn btn-secondary btn-small" onClick={() => openEdit(r)}>Edit</button>}
                        {!r.is_system && can('roles_users', 'delete') && <button className="btn btn-danger btn-small" onClick={() => setDeleteConfirm(r.id)}>Delete</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '760px', width: '95%' }}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Role' : 'Add New Role'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><FiX /></button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: '14px' }}>
              {error && <div className="alert alert-error">{error}</div>}
              <div className="form-row">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Role Name *</label>
                  <input className="form-control" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Hostel Manager" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Description</label>
                  <input className="form-control" value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description" />
                </div>
              </div>

              <div className="table-container" style={{ marginTop: '6px' }}>
                <table className="perm-matrix">
                  <thead>
                    <tr>
                      <th>Module</th>
                      {ACTIONS.map(a => <th key={a.key}>{a.label}</th>)}
                      <th>All</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map(m => (
                      <tr key={m.key}>
                        <td>{m.label}</td>
                        {ACTIONS.map(a => (
                          <td key={a.key}>
                            <input type="checkbox" checked={!!permissions[m.key]?.[a.key]} onChange={() => togglePerm(m.key, a.key)} />
                          </td>
                        ))}
                        <td>
                          <button type="button" className="btn btn-secondary btn-small" onClick={() => toggleAllForModule(m.key, !(permissions[m.key]?.can_view && permissions[m.key]?.can_create && permissions[m.key]?.can_edit && permissions[m.key]?.can_delete))}>
                            Toggle
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : (editing ? 'Update Role' : 'Create Role')}</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header"><h3>Confirm Delete</h3></div>
            <div className="modal-body"><p>Are you sure you want to delete this role? Users assigned to it must be reassigned first.</p></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
